import { useCallback, useLayoutEffect, useState, type RefObject } from "react";

function composerSlots(row: HTMLElement) {
  return {
    actions: row.querySelector<HTMLElement>("[data-composer-actions]"),
    leading: row.querySelector<HTMLElement>("[data-composer-leading]"),
  };
}

/** Measure in CSS pixels so canvas zoom does not affect wrapping decisions. */
export function useComposerTextareaLayout(
  inputRef: RefObject<HTMLTextAreaElement | null>,
  value: string,
) {
  const [isMultiLine, setIsMultiLine] = useState(false);
  const syncTextareaHeight = useCallback(() => {
    const input = inputRef.current;
    const row = input?.parentElement;
    if (!input || !row || row.clientWidth === 0) return;
    const { actions, leading } = composerSlots(row);
    if (!actions) return;
    const rowStyle = getComputedStyle(row);
    const inputStyle = getComputedStyle(input);
    const padding =
      parseFloat(rowStyle.paddingLeft) + parseFloat(rowStyle.paddingRight);
    const columnGap = parseFloat(rowStyle.columnGap || "0");
    const leadingWidth = leading ? leading.offsetWidth + columnGap : 0;
    const available =
      row.clientWidth -
      padding -
      actions.offsetWidth -
      columnGap -
      leadingWidth;
    if (available <= 0) return;
    input.style.width = `${available}px`;
    input.style.height = "0px";
    const lineHeight = parseFloat(inputStyle.lineHeight) || 20;
    const singleHeight =
      lineHeight +
      parseFloat(inputStyle.paddingTop) +
      parseFloat(inputStyle.paddingBottom);
    setIsMultiLine(input.scrollHeight > singleHeight + 1);
    input.style.width = "100%";
    input.style.height = "0px";
    input.style.height = `${Math.min(input.scrollHeight, 168)}px`;
  }, [inputRef]);

  useLayoutEffect(() => {
    syncTextareaHeight();
  }, [value, isMultiLine, syncTextareaHeight]);

  useLayoutEffect(() => {
    const row = inputRef.current?.parentElement;
    if (!row) return;
    const { actions, leading } = composerSlots(row);
    if (!actions) return;
    const measure = () =>
      `${row.clientWidth}:${actions.offsetWidth}:${leading?.offsetWidth ?? 0}`;
    let lastWidths = measure();
    const observer = new ResizeObserver(() => {
      const widths = measure();
      if (widths === lastWidths) return;
      lastWidths = widths;
      syncTextareaHeight();
    });
    observer.observe(row);
    observer.observe(actions);
    if (leading) observer.observe(leading);
    return () => observer.disconnect();
  }, [inputRef, syncTextareaHeight]);

  return { isMultiLine, syncTextareaHeight };
}
